import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/auth/AuthContext';
import { useTranslation } from 'react-i18next';
import LoginPage from './components/auth/LoginPage';
import VersionFooter from './components/VersionFooter';

/**
 * Applies a signed-in user's PERSISTED language — once, when that identity arrives.
 *
 * 🔴 IT MUST NOT RE-ASSERT. It used to run `changeLanguage(user.language)` on every render where the two
 * disagreed, which made the language picker dead app-wide: the picker changed i18n, the auth user's
 * `language` was still the old value, and the next render changed it straight back. The user saw the
 * picker snap to Español and bounce back to English, and only a manual reload ever applied a choice.
 *
 * So the effect keys on the IDENTITY, not on the language value. Once a user's persisted language has been
 * applied, this component is done for that user, and a live pick is the user's to make. A different user
 * (or a re-login) re-arms it. `appliedFor` is a ref, not state, because re-applying must not itself cause
 * a render that re-runs the check.
 */
const I18nWatcher: React.FC = () => {
  const { user } = useAuth();
  const { i18n } = useTranslation('common');
  const appliedFor = React.useRef<string | null>(null);

  React.useEffect(() => {
    const identity = user?.id ?? user?.email ?? null;
    if (!identity) { appliedFor.current = null; return; }   // signed out — re-arm for the next identity
    if (appliedFor.current === identity) return;            // already applied for this user; never again
    appliedFor.current = identity;
    if (user?.language && user.language !== i18n.language) {
      i18n.changeLanguage(user.language);
    }
  }, [user?.id, user?.email, user?.language, i18n]);

  return null;
};
import LobbyPage from './components/auth/LobbyPage';
import AccountExistsPage from './components/auth/AccountExistsPage';
import AgentConsole from './components/agent-console';
import OnboardingWizard from './components/onboarding/OnboardingWizard';
import PlansPage from './components/billing/PlansPage';

// Backoffice
import { BackofficeLayout } from './components/backoffice/layout/BackofficeLayout';
import { CustomersPage } from './components/backoffice/pages/CustomersPage';
import { CustomerTelemetryPage } from './components/backoffice/pages/CustomerTelemetryPage';
import WhatsAppConnectPage from './components/backoffice/pages/WhatsAppConnectPage';

const ProtectedRoute: React.FC<{ children: React.ReactNode, allowSignup?: boolean, allowOnboarding?: boolean }> = ({ children, allowSignup = false, allowOnboarding = false }) => {
  const { user, accountId, loading } = useAuth();
  const location = useLocation();

  if (loading) return (
    <div className="min-h-screen bg-[#121212] flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#2cf1ff]"></div>
    </div>
  );

  if (!user) return <Navigate to="/login" />;

  // Onboarding wizard: require auth only. NOT the allowSignup "bounce once an account exists"
  // rule — the wizard creates the account at Step 0 and the user must stay to finish steps 1–5.
  if (allowOnboarding) return <>{children}</>;

  // If on signup page, don't redirect to signup. But if they have an account selected, go to home.
  if (allowSignup) {
    if (accountId && user.accounts.length > 0) return <Navigate to="/" />;
    return <>{children}</>;
  }

  // Allow Super Admins to access Backoffice without an accountId
  const isBackofficeRoute = window.location.pathname.startsWith('/backoffice');
  if (user.isSuperAdmin && isBackofficeRoute) {
    return <>{children}</>;
  }

  // Normal protected routes (Console, Lobby). A brand-new user with no account is sent into
  // the Phase-2 getting-started wizard (was /signup).
  if (!accountId && user.accounts.length === 0) return <Navigate to="/onboarding" />;
  if (!accountId && location.pathname !== '/lobby') return <Navigate to="/lobby" />;

  return <>{children}</>;
};

function App() {
  return (
    <AuthProvider>
      <I18nWatcher />
      <VersionFooter />
      <BrowserRouter>
        <React.Suspense fallback={
          <div className="min-h-screen bg-[#121212] flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#2cf1ff]"></div>
          </div>
        }>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/lobby" element={
              <ProtectedRoute>
                <LobbyPage />
              </ProtectedRoute>
            } />
            {/* Signup landed on an existing email → "account exists — log in or create separate".
                ProtectedRoute: only an AUTHENTICATED user (who proved they own the email) can reach it. */}
            <Route path="/welcome-back" element={
              <ProtectedRoute allowOnboarding>
                <AccountExistsPage />
              </ProtectedRoute>
            } />
            {/* /signup is RETIRED (legacy "Create Your Account" form w/ the Stripe-ID billingDetails
                field). Any path to it — a bookmarked URL, LobbyPage "add account" — now lands in the
                Phase-2 OnboardingWizard. Auth is enforced by the /onboarding route's ProtectedRoute. */}
            <Route path="/signup" element={<Navigate to="/onboarding" replace />} />
            <Route path="/onboarding" element={
              <ProtectedRoute allowOnboarding>
                <OnboardingWizard />
              </ProtectedRoute>
            } />
            <Route path="/plans" element={
              <ProtectedRoute>
                <PlansPage />
              </ProtectedRoute>
            } />

            {/* Backoffice Routes */}
            <Route
              path="/backoffice/*"
              element={
                <ProtectedRoute>
                  <BackofficeLayout>
                    <Routes>
                      <Route index element={<Navigate to="customers" replace />} />
                      <Route path="customers" element={<CustomersPage />} />
                      <Route path="customers/:id" element={<CustomerTelemetryPage />} />
                      <Route path="whatsapp" element={<WhatsAppConnectPage />} />
                      <Route path="*" element={<Navigate to="customers" replace />} />
                    </Routes>
                  </BackofficeLayout>
                </ProtectedRoute>
              }
            />

            <Route path="/" element={
              <ProtectedRoute>
                <AgentConsole />
              </ProtectedRoute>
            } />
          </Routes>
        </React.Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;